import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../Config';

// Composants d'alerte (à importer ou définir ici)
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 4000);

    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-emerald-500' : 
                  type === 'error' ? 'bg-rose-500' : 
                  'bg-blue-500';

  return (
    <div className={`fixed top-6 right-6 z-50 px-4 py-3 ${bgColor} text-white rounded-lg shadow-lg transform transition-transform duration-300 animate-slide-in`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          {type === 'success' && (
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
          {type === 'error' && (
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
          <span className="font-medium">{message}</span>
        </div>
        <button 
          onClick={onClose}
          className="ml-4 text-white hover:text-gray-200 focus:outline-none"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const ConfirmDialog = ({ isOpen, title, message, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-black/40"
        onClick={onCancel}
      />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="mt-2 text-gray-600 text-sm">{message}</p>
        </div>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-rose-600 hover:bg-rose-700 rounded-lg transition-colors"
          >
            Confirmer
          </button>
        </div>
      </div>
    </div>
  );
};

export default function Materiels() {
  const [materiels, setMateriels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingMat, setEditingMat] = useState(null);
  const [showStockModal, setShowStockModal] = useState(null);
  const [lowStock, setLowStock] = useState([]);

  const [filters, setFilters] = useState({
    category_id: '', type: '', actif: ''
  });

  const [form, setForm] = useState({
    category_id: '', nom: '', type: 'consomable', unite: '', stock: 0,
    stock_minimum: 5, est_reparable: false
  });

  const [stockForm, setStockForm] = useState({ type: 'ajout', quantite: 1, motif: '' });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [confirm, setConfirm] = useState({ 
    show: false, 
    title: '', 
    message: '', 
    onConfirm: null 
  });
  
  const navigate = useNavigate();

  const token = localStorage.getItem('token');
  const headers = { 
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  useEffect(() => {
    fetchCategories();
    fetchMateriels();
    fetchLowStock();
  }, [filters]);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const showConfirm = (title, message, onConfirm) => {
    setConfirm({ show: true, title, message, onConfirm });
  };

  const hideToast = () => {
    setToast({ ...toast, show: false });
  };

  const hideConfirm = () => {
    setConfirm({ ...confirm, show: false });
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/categories`, { headers });
      const data = await response.json();
      setCategories(data.data || []);
    } catch (err) { 
      console.error('Erreur catégories:', err);
      showToast('Erreur lors du chargement des catégories', 'error');
    }
  };

  const fetchMateriels = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/materiels?per_page=100`;
      if (filters.category_id) url += `&category_id=${filters.category_id}`;
      if (filters.type) url += `&type=${filters.type}`;
      if (filters.actif !== '') url += `&actif=${filters.actif}`;

      const response = await fetch(url, { headers });
      const data = await response.json();
      setMateriels(data.data?.data || data.data || []);
    } catch (err) { 
      console.error('Erreur matériels:', err);
      showToast('Erreur lors du chargement des matériels', 'error');
    } finally { 
      setLoading(false); 
    }
  };

  const fetchLowStock = async () => {
    try {
      const response = await fetch(`${API_URL}/materiels/stock/faible`, { headers });
      const data = await response.json();
      setLowStock(data.data || []);
    } catch (err) { 
      console.error('Erreur stock faible:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingMat) {
        const response = await fetch(`${API_URL}/materiels/${editingMat.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(form)
        });
        if (!response.ok) throw new Error('Erreur mise à jour');
        showToast('Matériel mis à jour avec succès');
      } else {
        const response = await fetch(`${API_URL}/materiels`, {
          method: 'POST',
          headers,
          body: JSON.stringify(form)
        });
        if (!response.ok) throw new Error('Erreur création');
        showToast('Matériel créé avec succès');
      }
      resetForm();
      fetchMateriels();
    } catch (err) {
      showToast('Erreur lors de l\'opération', 'error');
      console.error('Erreur:', err);
    }
  };

  const resetForm = () => {
    setForm({ 
      category_id: '', 
      nom: '', 
      type: 'consomable', 
      unite: '', 
      stock: 0, 
      stock_minimum: 5, 
      est_reparable: false 
    });
    setShowForm(false);
    setEditingMat(null);
  };

  const handleEdit = (m) => {
    setEditingMat(m);
    setForm({
      category_id: m.category_id,
      nom: m.nom,
      type: m.type,
      unite: m.unite,
      stock: m.stock,
      stock_minimum: m.stock_minimum,
      est_reparable: m.est_reparable
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (m) => {
    showConfirm(
      'Supprimer le matériel',
      `Êtes-vous sûr de vouloir supprimer "${m.nom}" ?`,
      async () => {
        try {
          const response = await fetch(`${API_URL}/materiels/${m.id}`, {
            method: 'DELETE',
            headers
          });
          if (!response.ok) throw new Error('Erreur suppression');
          showToast('Matériel supprimé avec succès');
          fetchMateriels();
        } catch (err) {
          showToast('Impossible de supprimer ce matériel', 'error');
          console.error('Erreur:', err);
        } finally {
          hideConfirm();
        }
      }
    );
  };

  const handleStockUpdate = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/materiels/${showStockModal.id}/stock`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(stockForm)
      });
      if (!response.ok) throw new Error('Erreur stock');
      showToast('Stock mis à jour avec succès');
      setShowStockModal(null);
      setStockForm({ type: 'ajout', quantite: 1, motif: '' });
      fetchMateriels();
      fetchLowStock();
    } catch (err) {
      showToast('Erreur lors de la mise à jour du stock', 'error');
      console.error('Erreur:', err);
    }
  };

  const stockStatus = (stock, min) => {
    if (stock === 0) return { text: 'Épuisé', color: 'text-rose-600', bg: 'bg-rose-50' };
    if (stock <= min) return { text: `Faible (${stock})`, color: 'text-amber-600', bg: 'bg-amber-50' };
    return { text: `OK (${stock})`, color: 'text-emerald-600', bg: 'bg-emerald-50' };
  };

  const typeBadge = (type) => {
    const styles = {
      consomable: 'bg-blue-100 text-blue-800',
      pert: 'bg-purple-100 text-purple-800',
      usage: 'bg-green-100 text-green-800'
    };
    return styles[type] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Toast Notification */}
      {toast.show && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={hideToast}
        />
      )}

      {/* Confirmation Dialog */}
      {confirm.show && (
        <ConfirmDialog
          isOpen={confirm.show}
          title={confirm.title}
          message={confirm.message}
          onConfirm={confirm.onConfirm}
          onCancel={hideConfirm}
        />
      )}

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-2">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Matériels</h1>
              <p className="text-gray-500 mt-1">Gestion du stock et de l'inventaire</p>
            </div>
            <button
              onClick={() => { 
                resetForm(); 
                setShowForm(true); 
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              + Nouveau matériel
            </button>
          </div>
          
          {/* Alertes stock faible */}
          {lowStock.length > 0 && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-amber-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.998-.833-2.73 0L4.34 16.5c-.77.833.192 2.5 1.732 2.5" />
                </svg>
                <div className="text-sm font-medium text-amber-800">
                  {lowStock.length} matériel{lowStock.length > 1 ? 's' : ''} en stock faible
                </div>
              </div>
              <div className="mt-2 text-xs text-amber-700">
                {lowStock.map((m, i) => (
                  <span key={m.id}>
                    {m.nom} ({m.stock}/{m.stock_minimum})
                    {i < lowStock.length - 1 ? ' • ' : ''}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-4 gap-4 mt-6">
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-semibold text-gray-900">{materiels.length}</div>
              <div className="text-sm text-gray-500">Total matériels</div>
            </div>
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-semibold text-emerald-600">
                {materiels.filter(m => m.actif).length}
              </div>
              <div className="text-sm text-gray-500">Actifs</div>
            </div>
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-semibold text-amber-600">
                {lowStock.length}
              </div>
              <div className="text-sm text-gray-500">Stock faible</div>
            </div>
            <div className="bg-white p-4 rounded-lg border">
              <div className="text-2xl font-semibold text-blue-600">
                {materiels.reduce((sum, m) => sum + (m.stock || 0), 0)}
              </div>
              <div className="text-sm text-gray-500">Quantité totale</div>
            </div>
          </div>
        </div>

        {/* Filtres */}
        <div className="mb-6 bg-white p-4 rounded-lg border">
          <div className="text-sm font-medium text-gray-700 mb-3">Filtres</div>
          <div className="flex flex-wrap gap-3">
            <select 
              value={filters.category_id} 
              onChange={e => setFilters({ ...filters, category_id: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Toutes catégories</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>{c.nom}</option>
              ))}
            </select>
            
            <select 
              value={filters.type} 
              onChange={e => setFilters({ ...filters, type: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Tous types</option>
              <option value="consomable">Consommable</option>
              <option value="pert">PERT</option>
              <option value="usage">Usage</option>
            </select>
            
            <select 
              value={filters.actif} 
              onChange={e => setFilters({ ...filters, actif: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Tous statuts</option>
              <option value="1">Actif</option>
              <option value="0">Inactif</option>
            </select>
          </div>
        </div>

        {/* Formulaire */}
        {showForm && (
          <div className="mb-8 bg-white rounded-xl border p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingMat ? 'Modifier' : 'Nouveau matériel'}
              </h2>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Catégorie *
                  </label>
                  <select 
                    value={form.category_id} 
                    onChange={e => setForm({ ...form, category_id: e.target.value })} 
                    required 
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Sélectionner une catégorie</option>
                    {categories.map(c => (
                      <option key={c.id} value={c.id}>{c.nom}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom *
                  </label>
                  <input 
                    placeholder="Nom du matériel" 
                    value={form.nom} 
                    onChange={e => setForm({ ...form, nom: e.target.value })} 
                    required 
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type
                  </label>
                  <select 
                    value={form.type} 
                    onChange={e => setForm({ ...form, type: e.target.value })} 
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="consomable">Consommable</option>
                    <option value="pert">PERT</option>
                    <option value="usage">Usage</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unité *
                  </label>
                  <input 
                    placeholder="Ex: pièce, kg, litre" 
                    value={form.unite} 
                    onChange={e => setForm({ ...form, unite: e.target.value })} 
                    required 
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Stock initial *
                  </label>
                  <input 
                    type="number" 
                    placeholder="Quantité" 
                    value={form.stock} 
                    onChange={e => setForm({ ...form, stock: parseInt(e.target.value) || 0 })} 
                    required 
                    min="0"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Stock minimum *
                  </label>
                  <input 
                    type="number" 
                    placeholder="Seuil d'alerte" 
                    value={form.stock_minimum} 
                    onChange={e => setForm({ ...form, stock_minimum: parseInt(e.target.value) || 0 })} 
                    required 
                    min="0"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div className="flex items-center">
                <input 
                  type="checkbox" 
                  id="reparable"
                  checked={form.est_reparable} 
                  onChange={e => setForm({ ...form, est_reparable: e.target.checked })} 
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="reparable" className="ml-2 text-sm text-gray-600">
                  Matériel réparable
                </label>
              </div>
              
              <div className="flex gap-3 pt-2">
                <button 
                  type="submit" 
                  className="px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                >
                  {editingMat ? 'Mettre à jour' : 'Créer'}
                </button>
                <button 
                  type="button" 
                  onClick={resetForm} 
                  className="px-5 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Matériel</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Catégorie</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Type</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Unité</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Stock</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Statut</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {materiels.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="text-gray-400 mb-3">
                        <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                      </div>
                      <p className="text-gray-500 mb-4">Aucun matériel disponible</p>
                      <button
                        onClick={() => setShowForm(true)}
                        className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                      >
                        Créer le premier
                      </button>
                    </td>
                  </tr>
                ) : (
                  materiels.map(m => {
                    const status = stockStatus(m.stock, m.stock_minimum);
                    return (
                      <tr key={m.id} className="hover:">
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900">{m.nom}</div>
                          <div className="text-xs text-gray-500 mt-0.5">ID: {m.id}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-600">
                            {m.category?.nom || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`text-xs px-3 py-1 rounded-full ${typeBadge(m.type)}`}>
                            {m.type}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-600">{m.unite}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className={`px-3 py-1 rounded-full text-sm ${status.bg}`}>
                            <span className={status.color}>{status.text}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                            m.actif 
                              ? 'bg-emerald-100 text-emerald-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {m.actif ? 'Actif' : 'Inactif'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit(m)}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium px-3 py-1 hover:bg-blue-50 rounded"
                            >
                              Modifier
                            </button>
                            <button
                              onClick={() => setShowStockModal(m)}
                              className="text-purple-600 hover:text-purple-800 text-sm font-medium px-3 py-1 hover:bg-purple-50 rounded"
                            >
                              Stock
                            </button>
                            <button
                              onClick={() => handleDelete(m)}
                              className="text-rose-600 hover:text-rose-800 text-sm font-medium px-3 py-1 hover:bg-rose-50 rounded"
                            >
                              Supprimer
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          
          {materiels.length > 0 && (
            <div className="px-6 py-4 border-t">
              <div className="flex justify-between items-center text-sm text-gray-600">
                <div>
                  {materiels.length} matériel{materiels.length > 1 ? 's' : ''}
                </div>
                <div className="flex items-center gap-4">
                  <div>
                    Faible stock: <span className="font-medium">{lowStock.length}</span>
                  </div>
                  <div>
                    Total quantité: <span className="font-medium">
                      {materiels.reduce((sum, m) => sum + (m.stock || 0), 0)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal Stock */}
      {showStockModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowStockModal(null)}
          />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Gestion du stock
                </h3>
                <p className="text-sm text-gray-500 mt-1">{showStockModal.nom}</p>
              </div>
              <button
                onClick={() => setShowStockModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleStockUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type d'opération
                </label>
                <select 
                  value={stockForm.type} 
                  onChange={e => setStockForm({ ...stockForm, type: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="ajout">Ajout au stock (+)</option>
                  <option value="retrait">Retrait du stock (-)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quantité
                </label>
                <input 
                  type="number" 
                  placeholder="Quantité" 
                  value={stockForm.quantite} 
                  onChange={e => setStockForm({ ...stockForm, quantite: parseInt(e.target.value) || 1 })} 
                  min="1" 
                  required 
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Motif <span className="text-gray-400 font-normal">(optionnel)</span>
                </label>
                <input 
                  type="text" 
                  placeholder="Ex: Réapprovisionnement, Utilisation..." 
                  value={stockForm.motif} 
                  onChange={e => setStockForm({ ...stockForm, motif: e.target.value })} 
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button 
                  type="submit" 
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                >
                  Valider
                </button>
                <button 
                  type="button" 
                  onClick={() => setShowStockModal(null)} 
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
}